import React from 'react';
import { useDispatch } from 'react-redux';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { showConfirmCancelDialog, Utils } from 'app/main/components';
import { useMountEffect, usePagination, useSelection, useSettings } from 'app/main/components/hooks';
import { DetailsAdditionalPropsContext, ViewTable } from 'app/main/components/page-layouts';
import type { ViewFooterCustomInfoProps } from 'app/main/components/page-layouts/view/components/ViewFooter';
import type { MiddlewhereFilter } from 'app/main/components/filter/FilterItemManager';

import OrdersLinesTableFooterInfo from '../../components/OrdersLinesTableFooterInfo';
import useOrdersBillingTableColumns from '../../billing/OrdersBillingTableColumns';
import OrdersBillingTableActions from '../../billing/OrdersBillingTableActions';

interface OrdersBillingTabProps {
	order: NonNullable<any>;
	updateRefresh: VoidFunction;
}

export default function OrdersBillingTab({ order, updateRefresh }: OrdersBillingTabProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const authorizer = React.useContext(AuthorizerContext);

	const defaultFilters: MiddlewhereFilter[] = [
		{ filt_field: 'orbi_ords_uid', filt_clause: 'equals', filt_value: order.ords_uid }
	];
	const paginationProps = usePagination({ module: 'orders_billing', filter: { defaultFilters } });

	const isEditable = [1, 3].includes(order.ords_orst_id);
	const canUpdate = authorizer.can('update', 'orders') && isEditable;

	const { changeAdditionalProp } = React.useContext<any>(DetailsAdditionalPropsContext);

	const { selectedItems: selectedLines, setSelectedItems: setSelectedLines } = useSelection({
		module: 'orders_billing',
		isSelecting: canUpdate,
		editCallback: newSelectedLines => changeAdditionalProp?.('selectedOrderBillingLines', newSelectedLines || [])
	});

	const columns = useOrdersBillingTableColumns();

	if (isEditable && selectedLines.length === 0) {
		columns.push({
			field: 'actions',
			type: 'actions',
			width: 40,
			getActions: rowProps => OrdersBillingTableActions({ rowProps, order, updateRefresh }),
			hideable: false
		});
	}

	const showDeleteBillingDialog = (lines: any[]) => {
		dispatch(
			showConfirmCancelDialog({
				title: lines?.length > 1 ? t('DIALOG_TITLE_DELETE_MULTIPLE') : t('DIALOG_TITLE_DELETE_GENERIC'),
				onConfirm: () => {
					dispatch(Utils.alertProgressMsg(t));
					Utils.executeStored({
						action: 'st_orders_billing_delete',
						dispatch,
						data: {
							ords_uid: order.ords_uid,
							ids: lines.map(orbi => orbi.orbi_uid)
						},
						successHandler: () => {
							dispatch(Utils.alertSuccessMsg(t));
							setSelectedLines([]);
							updateRefresh();
						}
					});
				}
			})
		);
	};

	useMountEffect(() => {
		changeAdditionalProp('setSelectedLines', setSelectedLines);
		changeAdditionalProp('showDeleteBillingDialog', showDeleteBillingDialog);
	});

	const FooterCustomInfo = React.useCallback(({ additionalInfo }: ViewFooterCustomInfoProps) => {
		return <OrdersLinesTableFooterInfo totTaxExcl={additionalInfo.tot_value_tax_excl} />;
	}, []);

	return (
		<ViewTable
			id="ordersBillingView"
			module="orders_billing"
			columns={columns}
			paginationProps={paginationProps}
			slots={{ footerCustomInfo: FooterCustomInfo }}
			selectionProps={canUpdate ? { mode: 'multiple', selectedItems: selectedLines } : undefined}
		/>
	);
}
